import { monthPoints } from './evidence.helper';

/** Filtra las preguntas contables sin padres duplicados. */
export function countableQuestions(questions: any[]): any[] {
  return dropParents(questions || [], parentIds(questions || []));
}

/** Recolecta los ids de preguntas que tienen hijas. */
function parentIds(questions: any[]): Set<string> {
  const ids = new Set<string>();
  for (const item of questions) addParentId(ids, item);
  return ids;
}

/** Registra el id padre de una pregunta si existe. */
function addParentId(ids: Set<string>, item: any): void {
  if (item.parentId) ids.add(item.parentId);
}

/** Descarta las preguntas que agrupan subpreguntas. */
function dropParents(questions: any[], parents: Set<string>): any[] {
  const out: any[] = [];
  for (const item of questions) collectCountable(out, item, parents);
  return out;
}

/** Conserva la pregunta si no agrupa subpreguntas. */
function collectCountable(out: any[], item: any, parents: Set<string>): void {
  if (!parents.has(item.id)) out.push(item);
}

/** Suma los pesos de las preguntas contables. */
function sumWeights(questions: any[]): number {
  let total = 0;
  for (const item of (questions || [])) total += item.score || 1;
  return total;
}

/** Construye el mapa de respuestas por pregunta. */
function buildAnswerMap(answers: any[]): Record<string, any> {
  const map: Record<string, any> = {};
  for (const ans of (answers || [])) map[ans.questionId] = ans;
  return map;
}

/** Obtiene los puntos mensuales de una pregunta. */
function monthlyItemPoints(q: any, map: Record<string, any>): number {
  return monthPoints(q, map[q.id]);
}

/** Suma el puntaje mensual ponderado obtenido. */
function sumMonthlyObtained(countable: any[], answers: any[]): number {
  const map = buildAnswerMap(answers);
  let total = 0;
  for (const item of countable) total += (item.score || 1) * monthlyItemPoints(item, map);
  return total;
}

/** Calcula el puntaje mensual en porcentaje. */
export function monthlyScore(questions: any[], answers: any[]): number {
  const countable = countableQuestions(questions);
  const total = sumWeights(countable);
  if (!total) return 0;
  const obtained = sumMonthlyObtained(countable, answers);
  return Math.round((obtained / total) * 100);
}

/** Calcula el avance simple contra una meta. */
function singleAnnualPercent(cumul: number, target: number): number {
  if (!target) return 0;
  return Math.min(cumul / target, 1) * 100;
}

/** Promedia el avance de la meta general y personal. */
function combinedAnnualPercent(cumul: number, general: number, personal: number): number {
  return (singleAnnualPercent(cumul, general) + singleAnnualPercent(cumul, personal)) / 2;
}

/** Resuelve el porcentaje anual de una pregunta. */
function annualItemPercent(q: any, cumul: number, goalMap: Record<string, number>): number {
  return blendedPercent(q, cumul, goalOf(goalMap, q.id));
}

/** Obtiene la meta personal de la pregunta. */
function goalOf(goalMap: Record<string, number>, qId: string): number | null {
  if (!goalMap) return null;
  return goalMap[qId] || null;
}

/** Combina la meta general con la personal. */
function blendedPercent(q: any, cumul: number, personal: number | null): number {
  const general = generalGoal(q);
  if (personal && general) return combinedAnnualPercent(cumul, general, personal);
  return singleFor(blendedTarget(personal, general), cumul);
}

/** Obtiene la meta general de la pregunta. */
function generalGoal(q: any): number {
  return q.goalValue || q.score || 1;
}

/** Elige la meta personal o la general disponible. */
function blendedTarget(personal: number | null, general: number): number {
  return personal || general;
}

/** Calcula el avance simple para la meta elegida. */
function singleFor(target: number, cumul: number): number {
  return singleAnnualPercent(cumul, target);
}

/** Calcula el porcentaje anual público de una pregunta. */
export function annualQuestionPercent(q: any, cumul: number, goalMap: Record<string, number>): number {
  return annualItemPercent(q, cumul || 0, goalMap || {});
}

/** Suma el puntaje anual ponderado obtenido. */
function sumAnnualObtained(
  countable: any[],
  cumulMap: Record<string, number>,
  goalMap: Record<string, number>,
): number {
  let total = 0;
  for (const item of countable) total += annualItemOf(item, cumulMap, goalMap);
  return total;
}

/** Calcula el aporte anual ponderado de una pregunta. */
function annualItemOf(q: any, cumulMap: Record<string, number>, goalMap: Record<string, number>): number {
  return (q.score || 1) * annualItemPercent(q, cumulOf(cumulMap, q.id), goalMap) / 100;
}

/** Obtiene el acumulado de una pregunta o cero. */
function cumulOf(cumulMap: Record<string, number>, qId: string): number {
  return cumulMap?.[qId] || 0;
}

/** Calcula el puntaje anual en porcentaje topado a 100. */
export function annualScore(
  questions: any[],
  cumulMap: Record<string, number>,
  goalMap: Record<string, number>,
): number {
  const countable = countableQuestions(questions);
  const total = sumWeights(countable);
  if (!total) return 0;
  const obtained = sumAnnualObtained(countable, cumulMap, goalMap);
  return Math.round(Math.min((obtained / total) * 100, 100));
}

/** Busca el nivel correspondiente al puntaje. */
export function findLevel(score: number, levels: any[]): any | null {
  for (const level of (levels || [])) {
    if (inLevel(score, level)) return level;
  }
  return null;
}

/** Indica si el puntaje cae dentro del nivel. */
function inLevel(score: number, level: any): boolean {
  return score >= level.minPercent && score <= level.maxPercent;
}
